import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import { useEffect, useId, useState } from 'react';

export interface RedirectAllowListSectionProps {
  initialAllowedOrigins: string[];
  selectedCode: string;
  onAllowedOriginsSave: (allowedOrigins: string[]) => Promise<void>;
  dataTestId?: string;
}

const ORIGIN_HELPER_TEXT =
  'HTTPS origin only: scheme, domain, optional port. No path, query or wildcards.';
const SAVE_FALLBACK_ERROR = "Couldn't save. Nothing was changed.";

export function RedirectAllowListSection({
  initialAllowedOrigins,
  selectedCode,
  onAllowedOriginsSave,
  dataTestId = 'redirect-allow-list-section',
}: Readonly<RedirectAllowListSectionProps>) {
  const [isEditing, setIsEditing] = useState(false);
  const [stagedOrigins, setStagedOrigins] = useState<string[]>(initialAllowedOrigins);
  const [originInput, setOriginInput] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const helperId = useId();

  const hasOrigins = initialAllowedOrigins.length > 0;

  // Re-sync staged state when the source origins or the selected code change
  // (e.g. the admin switches org code). Reset everything and close the dialog.
  useEffect(() => {
    setStagedOrigins(initialAllowedOrigins);
    setIsEditing(false);
    setOriginInput('');
    setSaveError(null);
    setShowClearConfirm(false);
    setIsSaving(false);
  }, [initialAllowedOrigins, selectedCode]);

  const openEditor = () => {
    setStagedOrigins(initialAllowedOrigins);
    setOriginInput('');
    setSaveError(null);
    setShowClearConfirm(false);
    setIsSaving(false);
    setIsEditing(true);
  };

  const closeEditor = () => {
    setIsEditing(false);
    setShowClearConfirm(false);
  };

  const trimmedInput = originInput.trim();
  const addDisabled = trimmedInput.length === 0 || isSaving;

  const handleAdd = () => {
    if (addDisabled) {
      return;
    }
    setStagedOrigins((prev) => [...prev, trimmedInput]);
    setOriginInput('');
  };

  const handleInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAdd();
    }
  };

  const handleRemove = (index: number) => {
    if (stagedOrigins.length <= 1) {
      setShowClearConfirm(true);
      return;
    }
    setStagedOrigins((prev) => prev.filter((_, i) => i !== index));
  };

  const handleConfirmClear = () => {
    setStagedOrigins([]);
    setShowClearConfirm(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await onAllowedOriginsSave(stagedOrigins);
      closeEditor();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : SAVE_FALLBACK_ERROR);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box data-testid={dataTestId}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.semantic.spacing.xs,
        })}
      >
        <Typography variant="subtitle1">Allowed redirect origins</Typography>
        <IconButton
          size="small"
          onClick={openEditor}
          data-action="edit-allowed-origins"
          aria-label="Edit allowed redirect origins"
          sx={{ color: 'primary.main' }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography
        variant="caption"
        sx={(theme) => ({
          display: 'block',
          marginBottom: theme.semantic.spacing.sm,
          color: theme.palette.text.secondary,
        })}
      >
        Dynamic: partners send each customer to their own destination, limited to these origins.
      </Typography>

      {/*
        Read-mode rows render the same origin strings as the edit dialog and live in
        document.body behind the modal. queryByText traverses hidden/aria-hidden nodes, so
        the read-mode rows must NOT be in the DOM while editing, otherwise removal assertions
        match the background copy. Suppress them entirely when the dialog is open.
      */}
      {!isEditing &&
        (hasOrigins ? (
          <List dense disablePadding>
            {initialAllowedOrigins.map((origin) => (
              <ListItem
                key={origin}
                disableGutters
                sx={(theme) => ({
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  wordBreak: 'break-all',
                })}
              >
                <Typography variant="body2">{origin}</Typography>
              </ListItem>
            ))}
          </List>
        ) : (
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'flex-start',
              gap: theme.semantic.spacing.sm,
              padding: theme.semantic.spacing.sm,
            })}
          >
            <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Box>
              <Typography variant="subtitle1">
                No origins allowed: dynamic redirects are off.
              </Typography>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  display: 'block',
                  marginTop: theme.semantic.spacing.xs,
                  color: theme.palette.text.secondary,
                })}
              >
                Partners cannot send a customer to a per-invite destination for this code. The
                Return Path above is used instead, if set. Add an origin to turn dynamic redirects
                on.
              </Typography>
            </Box>
          </Box>
        ))}

      <Dialog
        open={isEditing}
        onClose={closeEditor}
        fullWidth
        maxWidth="sm"
        transitionDuration={0}
      >
        <DialogTitle>Allowed redirect origins</DialogTitle>
        <DialogContent>
          {saveError && (
            <Alert
              severity="error"
              sx={(theme) => ({ marginBottom: theme.semantic.spacing.sm })}
            >
              {saveError}
            </Alert>
          )}

          <List dense disablePadding>
            {stagedOrigins.map((origin, index) => (
              <ListItem
                key={`${origin}-${index}`}
                disableGutters
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleRemove(index)}
                    disabled={isSaving}
                    data-action="remove-origin"
                    aria-label={`Remove ${origin}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
                sx={(theme) => ({
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  wordBreak: 'break-all',
                })}
              >
                <Typography variant="body2">{origin}</Typography>
              </ListItem>
            ))}
          </List>

          <Box sx={(theme) => ({ marginTop: theme.semantic.spacing.base })}>
            <Box
              sx={(theme) => ({
                display: 'flex',
                // Helper text sits on its own row below, so the row height is the
                // input height and the Add button stretches to match it.
                alignItems: 'stretch',
                gap: theme.semantic.spacing.sm,
              })}
            >
              <TextField
                fullWidth
                size="small"
                label="Add an origin"
                placeholder="https://app.partner.com"
                value={originInput}
                onChange={(event) => setOriginInput(event.target.value)}
                onKeyDown={handleInputKeyDown}
                disabled={isSaving}
                inputProps={{ 'data-field': 'origin-input', 'aria-describedby': helperId }}
              />
              <Button
                variant="outlined"
                onClick={handleAdd}
                disabled={addDisabled}
                data-action="add-origin"
                // Drop the Clarity 48px min-height floor so the button stretches
                // down to the size="small" input height rather than overhanging it.
                sx={{ minHeight: 0 }}
              >
                Add
              </Button>
            </Box>
            <Typography
              id={helperId}
              variant="caption"
              sx={(theme) => ({
                display: 'block',
                marginTop: theme.semantic.spacing.xs,
                color: theme.palette.text.secondary,
              })}
            >
              {ORIGIN_HELPER_TEXT}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={closeEditor}
            disabled={isSaving}
            data-action="cancel-allowed-origins"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            data-action="save-allowed-origins"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        maxWidth="xs"
        transitionDuration={0}
        aria-labelledby="clear-allow-list-title"
        aria-describedby="clear-allow-list-description"
      >
        <DialogTitle id="clear-allow-list-title">Clear the allow-list?</DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-allow-list-description">
            Removing the last origin turns off dynamic redirects for this code. Partner per-invite
            destinations will be ignored. This takes effect when you save.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setShowClearConfirm(false)}>
            Keep origin
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmClear}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
